import React, { useState, useEffect } from 'react';
import { X, Clock, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AddHoursDialog = ({ isOpen, onClose, project, onSuccess, onCreateTask }) => {
  const { token, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      fetchProjectTasks();
    }
  }, [isOpen, project]);

  const fetchProjectTasks = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch tasks with pagination - fetch multiple pages if needed
      let allTasks = [];
      let currentPage = 1;
      let totalPages = 1;
      
      while (currentPage <= totalPages && currentPage <= 10) { // Max 10 pages (250 tasks)
        const response = await axios.get(`${API}/api/tasks`, { 
          headers,
          params: { 
            page: currentPage,
            rows_per_page: 25
          }
        });
        
        const data = response.data;
        const records = data?.records || [];
        allTasks = [...allTasks, ...records];
        
        totalPages = data?.total_pages || 1;
        currentPage++;
        
        // If we got less than requested, we're at the end
        if (records.length < 25) break;
      }
      
      console.log(`Fetched ${allTasks.length} total tasks`);
      console.log(`Project ID: ${project?.id}, Name: ${project?.field_5_raw || project?.field_2_raw || 'unknown'}`);
      
      // Filter tasks for this specific project
      // Tasks can be linked via field_48_raw (project reference)
      const projectTasks = allTasks.filter(task => {
        const taskProjectLink = task.field_48_raw?.[0]; // Project field in task
        if (!taskProjectLink) return false;
        
        // Check if task's project ID matches our project ID
        return taskProjectLink.id === project?.id;
      });
      
      console.log(`Found ${projectTasks.length} tasks for this project`);
      setTasks(projectTasks);
      
      // Don't auto-select - let user choose
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      alert('Failed to load tasks. Please try again.');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedTask || !hours || !date) {
      alert('Please fill all fields');
      return;
    }

    try {
      setSubmitting(true);
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const hoursData = {
        field_36: parseFloat(hours),
        field_37: date,
        field_62_raw: [{ id: user.id, identifier: user.name || user.email }],
        field_43_raw: [{ id: selectedTask.id, identifier: selectedTask.field_43_raw || selectedTask.field_43 }]
      };

      await axios.post(`${API}/hours`, hoursData, { headers });
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to register hours:', error);
      alert('Failed to register hours. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Register Hours
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Project: <span className="font-medium text-gray-900 dark:text-white">
              {project?.field_5_raw || project?.field_2_raw || project?.identifier || 'Unknown'}
            </span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Select the task you worked on for this project
          </p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading tasks...</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              No tasks found for this project. Create a task first to register hours.
            </p>
            <div className="flex gap-3 justify-center">
              {onCreateTask && (
                <button
                  onClick={() => {
                    onClose();
                    onCreateTask(project);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Create Task
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Task Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Task *
              </label>
              <select
                value={selectedTask?.id || ''}
                onChange={(e) => {
                  const task = tasks.find(t => t.id === e.target.value);
                  setSelectedTask(task);
                }}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a task</option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.field_43_raw || task.field_43 || 'Unnamed Task'}
                  </option>
                ))}
              </select>
            </div>

            {/* Hours Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hours *
              </label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                required
                placeholder="e.g., 2.5"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Registering...' : 'Register Hours'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddHoursDialog;
